import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { PURGE } from 'redux-persist'

/**
 * Organization feature flag that gates access to the Plaid HSA linking flow.
 * Mirrored on the Laravel side as `PlaidController::FEATURE_FLAG`.
 */
export const PLAID_HSA_LINK_FEATURE_FLAG = 'plaid_hsa_link'

export type PlaidItemStatus = 'active' | 'requires_reauth' | 'disconnected'

export type PlaidImportPolicy = 'all_transactions' | 'medical_only'

export interface PlaidAccountModel {
  id: string
  name: string | null
  mask: string | null
  subtype: string | null
  is_hsa_eligible: boolean
  // Persisted Redux state from before this field shipped will not have it;
  // call sites should coalesce to 'all_transactions' (today's behavior).
  // See CLAUDE.md "Redux Persist and Stale Schema".
  import_policy: PlaidImportPolicy
}

export interface PlaidItemModel {
  id: string
  institution_name: string | null
  status: PlaidItemStatus
  last_synced_at: string | null
  // Set when a sync job is dispatched; cleared implicitly when last_synced_at
  // advances past it. Persisted state predating this field will be undefined —
  // see CLAUDE.md "Redux Persist and Stale Schema".
  sync_started_at: string | null
  accounts: PlaidAccountModel[]
}

export interface PlaidState {
  items: PlaidItemModel[]
  loaded: boolean
}

const initialState: PlaidState = {
  items: [],
  loaded: false,
}

const plaidSlice = createSlice({
  name: 'plaid',
  initialState,
  reducers: {
    setPlaidItems: (state, action: PayloadAction<PlaidItemModel[]>) => {
      state.items = action.payload
      state.loaded = true
    },
    addPlaidItem: (state, action: PayloadAction<PlaidItemModel>) => {
      const existing = state.items.find((i) => i.id === action.payload.id)
      if (existing) {
        Object.assign(existing, action.payload)
      } else {
        state.items.push(action.payload)
      }
    },
    removePlaidItem: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((i) => i.id !== action.payload)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(PURGE, () => initialState)
  },
})

export const { setPlaidItems, addPlaidItem, removePlaidItem } = plaidSlice.actions
export default plaidSlice.reducer

export function redactPlaid(state: PlaidState): PlaidState {
  // Institution names / masks are not PHI; safe to forward to Sentry.
  return state
}
